import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { formatRelativeTime } from '../../lib/relativeTime';
import type { AuditTimelineEntry } from '../../types/auditTimeline';
import { getPlanActivity, type PlanActivityFilter } from './api';
import { describePlanActivity } from './describePlanActivity';

const FILTERS: Array<{ value: PlanActivityFilter; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'tasks', label: 'Tasks only' },
  { value: 'mine', label: 'My actions' },
];

// Slice B §6 Activity surface. A scrolling Section on the Plan detail page
// (decision #1 — section, not a tab). Reverse-chronological, friendly
// formatting via describePlanActivity, three filters (§6.5). Audit entries
// are append-only so refetchOnWindowFocus keeps it fresh without manual
// invalidation from every mutation site.
export function PlanActivity({ planId }: { planId: string }) {
  const [filter, setFilter] = useState<PlanActivityFilter>('all');

  const query = useQuery({
    queryKey: ['plan', planId, 'activity', filter],
    queryFn: () => getPlanActivity(planId, filter),
    refetchOnWindowFocus: true,
  });

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <SelectField
          size="sm"
          aria-label="Filter activity"
          value={filter}
          onValueChange={(v) => setFilter(v as PlanActivityFilter)}
          options={FILTERS.map((f) => ({ value: f.value, label: f.label }))}
        />
      </div>

      {query.isLoading ? (
        <Spinner />
      ) : query.error ? (
        <p className="text-sm text-red-600">
          Failed to load activity: {(query.error as Error).message}
        </p>
      ) : !query.data || query.data.length === 0 ? (
        <p className="text-sm italic text-slate-400">
          {filter === 'all'
            ? 'No activity yet.'
            : 'No activity matches this filter.'}
        </p>
      ) : (
        <ol className="space-y-4">
          {query.data.map((e) => (
            <ActivityRow key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

function ActivityRow({ entry }: { entry: AuditTimelineEntry }) {
  const desc = describePlanActivity(entry);
  return (
    <li className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <time
        dateTime={entry.timestamp}
        title={new Date(entry.timestamp).toLocaleString()}
        className="pt-0.5 text-xs text-slate-500"
      >
        {formatRelativeTime(entry.timestamp)}
      </time>
      <div className="text-slate-800">
        <span className="mr-1.5" aria-hidden>
          {desc.icon}
        </span>
        {desc.summary}
      </div>
    </li>
  );
}
